import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Form, Input, DatePicker, InputNumber, Col, Row, notification, Upload, Image } from 'antd';
import { CloseOutlined, SaveOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import FormLabel from '../../../components/FormLabel';
import dayjs from 'dayjs';
import vn from 'antd/es/date-picker/locale/vi_VN';
import buddhistEra from 'dayjs/plugin/buddhistEra';
import CustomSelect from '../../../components/CustomSelect';
import NormalFormLabel from '../../../components/NormalFormLabel';
import { LIST_STATUS_CAMPAIGN } from '../../../constants/appSelect';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createUpdateCampaignRequest } from '../../../services/campaignRequest';
import { checkPermission } from '../../../utils/checkPermission';
import { useSelector } from 'react-redux';
import { getListAllPartnersRequest } from '../../../services/campaignStatisticRequest';
import { CREATE_UPDATE_CAMPAIGN } from '../../../constants/apiEndPoint';
import {
  getListAudienceRequest,
  getListCampaignsCMSNextRequest,
  getTokenToUploadRequest,
} from '../../../services/commonRequest';
import { uploadImageCDN } from '../../../utils/utility';
dayjs.extend(buddhistEra);

const ThemChienDich = () => {
  const [form] = Form.useForm();
  const maxType = Form.useWatch('max_type', form);
  const maxClick = Form.useWatch('max_click', form);
  const maxImpression = Form.useWatch('max_impression', form);
  const watchBanners = Form.useWatch('banners', form);
  const dataRedux = useSelector((state) => state.user);
  const accountPartnerId = dataRedux?.info?.partner_id;
  const accountType = dataRedux?.info?.type;
  const checkAccountIsPartner = accountType === 2 && accountPartnerId;
  const navigate = useNavigate();
  const buddhistLocale = {
    ...vn,
    lang: {
      ...vn.lang,
      fieldDateFormat: 'YYYY-MM-DD',
      fieldDateTimeFormat: 'YYYY-MM-DD HH:mm',
      yearFormat: 'YYYY',
      cellYearFormat: 'YYYY',
    },
  };

  // const { data: dataProvinces } = useQuery({
  //   queryKey: ['dataProvinces'],
  //   queryFn: () => getListProvincesRequest(),
  // });

  // // convert list permission group options filter
  // const dataProvincesOption = useMemo(() => {
  //   const options =
  //     dataProvinces?.map((item) => {
  //       return { label: item?.name, value: item?.id };
  //     }) || [];
  //   return options;
  // }, [dataProvinces]);

  // get data campaigns cms next
  const { data: dataAllCampaignsCMSNext } = useQuery({
    queryKey: ['dataAllCampaignsCMSNext'],
    queryFn: () => getListCampaignsCMSNextRequest(),
  });

  // convert list campaigns cms next options
  const dataAllCampaignsCMSNextOption = useMemo(() => {
    const options =
      dataAllCampaignsCMSNext
        ?.filter((item) => item?.status === 1)
        ?.map((item) => {
          return { label: item?.title, value: item?.id };
        }) || [];
    return options;
  }, [dataAllCampaignsCMSNext]);

  // get data partners
  const { data: dataAllPartners } = useQuery({
    queryKey: ['dataAllPartners'],
    queryFn: () => getListAllPartnersRequest(),
    enabled: !checkAccountIsPartner,
  });

  // convert list partners options
  const dataAllPartnersOption = useMemo(() => {
    const options =
      dataAllPartners
        ?.filter((item) => item?.status === 1)
        ?.map((item) => {
          return { label: item?.name, value: item?.id };
        }) || [];
    return options;
  }, [dataAllPartners]);

  // get data audiences
  const { data: audienceList } = useQuery({
    queryKey: ['audienceList'],
    queryFn: () => getListAudienceRequest(),
  });

  // convert list audience options
  const dataAudienceOptions = useMemo(() => {
    const options =
      audienceList?.map((item) => {
        return { label: item?.name, value: item?.code };
      }) || [];
    return options;
  }, [audienceList]);

  const changeBack = () => {
    navigate('/quan-ly-chien-dich');
  };

  // const validAgeRange = (_, value) => {
  //   const minAge = form.getFieldValue('min_age');
  //   if (value && minAge && value < minAge) {
  //     return Promise.reject('Độ tuổi kết thúc phải lớn hơn hoặc bằng độ tuổi bắt đầu');
  //   }
  //   return Promise.resolve();
  // };

  const validateTimeRange = (_, value) => {
    const startTime = form.getFieldValue('start_time');
    if (value && startTime) {
      const startTimeValue = dayjs(startTime); // Ensure dayjs object for comparison
      const endTimeValue = dayjs(value); // Ensure dayjs object for comparison
      if (startTimeValue.isAfter(endTimeValue)) {
        return Promise.reject('Thời gian kết thúc phải lớn hơn hoặc bằng thời gian bắt đầu');
      }
    }
    return Promise.resolve();
  };

  // handle submit form
  const { mutate: mutateCreateUpdateCampaign, isPending: isLoadingCreateCampaign } = useMutation({
    mutationFn: createUpdateCampaignRequest,
    onSuccess: async (data) => {
      if (data?.status) {
        notification.success({
          message: 'Thêm mới chiến dịch thành công',
        });
        navigate('/quan-ly-chien-dich');
      } else {
        notification.error({
          message: data?.showMessage || 'Có lỗi xảy ra vui lòng thử lại sau!',
        });
      }
    },
    onError: (error) => {
      notification.error({
        message: error.data?.showMessage || 'Có lỗi xảy ra vui lòng thử lại sau!',
      });
    },
  });

  const handleSubmitForm = (data) => {
    const dataSubmit = {
      name: data?.name,
      partner_id: data?.partner_id,
      campaign_id: data?.campaign_id,
      audience: data?.audience || [],
      // platform: data?.platform === -1 ? null : data?.platform,
      gender: data?.gender,
      // city_id: data?.city_id ? JSON.stringify(data?.city_id.map(String)) : null,
      start_time: dayjs(data?.start_time).format('YYYY-MM-DD HH:mm:ss'),
      end_time: dayjs(data?.end_time).format('YYYY-MM-DD HH:mm:ss'),
      // min_age: data?.min_age || null,
      // max_age: data?.max_age || null,
      max_type: data?.max_type,
      max_click: data?.max_type === 1 ? data?.max_click : null,
      max_impression: data?.max_type === 1 ? data?.max_impression : null,
      max_amount: data?.max_type === 2 ? data?.max_amount : null,
      unit_max_click: data?.unit_max_click || null,
      unit_max_impressions: data?.unit_max_impressions || null,
      unit_max_amount: data?.unit_max_amount || null,
      banners: data?.banners || [],
      total_group_banner: data?.banners?.length || 0,
      status: data?.status,
    };
    mutateCreateUpdateCampaign(dataSubmit);
  };

  const handleUploadFile = async (file, index) => {
    // const dataTokenToUpload = await getTokenToUploadRequest();

    // if (dataTokenToUpload?.status !== 1) {
    //   notification.error({
    //     message: dataTokenToUpload?.showMessage || 'Có lỗi xảy ra vui lòng thử lại sau!',
    //   });

    //   return false;
    // }

    const url = await uploadImageCDN(file, 'token');

    if (url) {
      notification.success({
        message: 'Tải lên thành công!',
      });
      const safeUrl = encodeURI(url);
      const banners = watchBanners || [];
      banners[index] = { ...banners[index], banner_url: safeUrl };
      form.setFieldValue('banners', banners);
    }

    return false;
  };

  return (
    <>
      {checkPermission(CREATE_UPDATE_CAMPAIGN, 'Tạo mới') && (
        <div className="w-full">
          <h1 className="font-semibold text-[24px] leading-[32px] gap-[10px] py-[10px]">Thêm chiến dịch quảng cáo</h1>
          <Form
            layout="vertical"
            name="create-campaign"
            form={form}
            onFinish={handleSubmitForm}
            requiredMark={false}
            scrollToFirstError={{ behavior: 'smooth', block: 'center', focus: true }}
          >
            <Row gutter={[48, 0]}>
              <Col xs={24}>
                <Form.Item
                  label={<FormLabel text="Tên chiến dịch quảng cáo" />}
                  name="name"
                  rules={[
                    {
                      required: true,
                      message: 'Vui lòng nhập tên chiến dịch quảng cáo',
                    },
                  ]}
                >
                  <Input allowClear placeholder="Nhập tên chiến dịch quảng cáo..." />
                </Form.Item>
              </Col>
              <Col xs={checkAccountIsPartner ? 0 : 12}>
                <Form.Item
                  label={<FormLabel text="Đối tác" />}
                  name="partner_id"
                  rules={[{ required: true, message: 'Vui lòng chọn đối tác' }]}
                  initialValue={checkAccountIsPartner ? accountPartnerId : null}
                  hidden={checkAccountIsPartner}
                >
                  <CustomSelect
                    placeholder="Chọn đối tác"
                    options={dataAllPartnersOption}
                    disabled={checkAccountIsPartner}
                  />
                </Form.Item>
              </Col>
              <Col xs={12}>
                <Form.Item
                  label={<FormLabel text="Chiến dịch ghi nhận order" />}
                  name="campaign_id"
                  rules={[{ required: true, message: 'Vui lòng chọn chiến dịch ghi nhận order' }]}
                >
                  <CustomSelect
                    placeholder="Chọn chiến dịch ghi nhận order"
                    allowClear
                    options={dataAllCampaignsCMSNextOption}
                  />
                </Form.Item>
              </Col>
              <Col xs={12}>
                <Form.Item label={<NormalFormLabel text="Giới tính" />} name="gender" initialValue={null}>
                  <CustomSelect
                    placeholder="Nền tảng"
                    options={[
                      { value: null, label: 'Tất cả' },
                      { value: 0, label: 'Nam' },
                      { value: 1, label: 'Nữ' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col xs={checkAccountIsPartner ? 24 : 12}>
                <Form.Item label={<NormalFormLabel text="Tập khách hàng" />} name="audience">
                  <CustomSelect
                    placeholder="Chọn tập khách hàng"
                    allowClear
                    mode="multiple"
                    options={dataAudienceOptions}
                  />
                </Form.Item>
              </Col>
              {/* <Col xs={checkAccountIsPartner ? 24 : 12}>
                <Form.Item label={<NormalFormLabel text="Tỉnh/ Thành phố" />} name="city_id">
                  <CustomSelect placeholder="Chọn tỉnh/Thành phố" options={dataProvincesOption} mode="multiple" />
                </Form.Item>
              </Col>
              <Col xs={12}>
                <Form.Item
                  label={<FormLabel text="Nền tảng" />}
                  name="platform"
                  rules={[{ required: true, message: 'Vui lòng chọn nền tảng' }]}
                  initialValue={-1}
                >
                  <CustomSelect
                    placeholder="Chọn nền tảng"
                    options={[
                      { value: -1, label: 'Tất cả' },
                      { value: 1, label: 'IOS' },
                      { value: 2, label: 'Android' },
                    ]}
                  />
                </Form.Item>
              </Col> */}

              <Col xs={12}>
                <Form.Item
                  label={<FormLabel text="Thời gian bắt đầu" />}
                  name="start_time"
                  rules={[{ required: true, message: 'Vui lòng chọn thời gian bắt đầu' }]}
                >
                  <DatePicker
                    placeholder="Chọn thời gian bắt đầu"
                    locale={buddhistLocale}
                    showTime
                    needConfirm={false}
                  />
                </Form.Item>
              </Col>
              <Col xs={12}>
                <Form.Item
                  label={<FormLabel text="Thời gian kết thúc" />}
                  name="end_time"
                  rules={[
                    { required: true, message: 'Vui lòng chọn thời gian kết thúc' },
                    { validator: validateTimeRange },
                  ]}
                >
                  <DatePicker
                    placeholder="Chọn thời gian kết thúc"
                    locale={buddhistLocale}
                    showTime
                    needConfirm={false}
                  />
                </Form.Item>
              </Col>
              {/* <Col xs={24}>
                <div className="flex w-full flex-row justify-between items-center">
                  <div className="flex-1">
                    <Form.Item
                      label={<NormalFormLabel text="Độ tuổi" />}
                      name="min_age"
                      rules={[
                        () => ({
                          validator(_, value) {
                            if (value && value < 1) {
                              return Promise.reject(new Error('Độ tuổi phải lớn hơn 0'));
                            }

                            return Promise.resolve();
                          },
                        }),
                      ]}
                    >
                      <InputNumber type="number" placeholder="Nhập từ độ tuổi" min={1} />
                    </Form.Item>
                  </div>

                  <div className="flex flex-col justify-start items-center w-12 h-full">
                    <Typography.Text className="items-center">Đến</Typography.Text>
                  </div>
                  <div className="flex-1">
                    <Form.Item
                      label={<NormalFormLabel text="" />}
                      name="max_age"
                      rules={[
                        () => ({
                          validator(_, value) {
                            if (value && value < 1) {
                              return Promise.reject(new Error('Độ tuổi phải lớn hơn 0'));
                            }

                            return Promise.resolve();
                          },
                        }),
                        { validator: validAgeRange },
                      ]}
                    >
                      <InputNumber type="number" placeholder="Nhập đến độ tuổi" min={1} />
                    </Form.Item>
                  </div>
                </div>
              </Col> */}
              <Col xs={24}>
                <Form.Item label={<NormalFormLabel text="Loại tối đa" />} name="max_type">
                  <CustomSelect
                    placeholder="Chọn loại tối đa"
                    options={[
                      { value: 1, label: 'Click/ Impression' },
                      { value: 2, label: 'Ngân sách' },
                    ]}
                  />
                </Form.Item>
              </Col>
              {maxType === 1 && (
                <>
                  <Col xs={12}>
                    <Form.Item
                      label={<FormLabel text="Số Click tối đa (Lượt)" />}
                      name="max_click"
                      rules={[{ required: !maxImpression && maxType === 1, message: 'Vui lòng nhập số click tối đa' }]}
                    >
                      <InputNumber placeholder="Nhập số click tối đa..." />
                    </Form.Item>
                  </Col>
                  <Col xs={12}>
                    <Form.Item
                      label={<FormLabel text="Đơn vị tính" />}
                      name="unit_max_click"
                      rules={[{ required: true, message: 'Vui lòng chọn đơn vị tính' }]}
                      initialValue={1}
                    >
                      <CustomSelect
                        placeholder="Chọn đơn vị tính"
                        options={[
                          { value: 1, label: 'Toàn chiến dịch' },
                          { value: 2, label: '/1 Ngày' },
                        ]}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={12}>
                    <Form.Item
                      label={<FormLabel text="Số Impression tối đa (Lượt)" />}
                      name="max_impression"
                      rules={[{ required: !maxClick && maxType === 1, message: 'Vui lòng nhập số click tối đa' }]}
                    >
                      <InputNumber placeholder="Nhập số impression tối đa..." />
                    </Form.Item>
                  </Col>
                  <Col xs={12}>
                    <Form.Item
                      label={<FormLabel text="Đơn vị tính" />}
                      name="unit_max_impressions"
                      rules={[{ required: !maxClick && maxType === 1, message: 'Vui lòng chọn đơn vị tính' }]}
                      initialValue={1}
                    >
                      <CustomSelect
                        placeholder="Chọn đơn vị tính"
                        options={[
                          { value: 1, label: 'Toàn chiến dịch' },
                          { value: 2, label: '/1 Ngày' },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                </>
              )}
              {maxType === 2 && (
                <>
                  <Col xs={12}>
                    <Form.Item
                      label={<FormLabel text="Ngân sách tối đa (VNĐ)" />}
                      name="max_amount"
                      rules={[{ required: maxType === 2, message: 'Vui lòng nhập ngân sách tối đa' }]}
                    >
                      <InputNumber placeholder="Nhập ngân sách tối đa..." />
                    </Form.Item>
                  </Col>
                  <Col xs={12}>
                    <Form.Item
                      label={<FormLabel text="Đơn vị tính" />}
                      name="unit_max_amount"
                      rules={[{ required: maxType === 2, message: 'Vui lòng chọn đơn vị tính' }]}
                      initialValue={1}
                    >
                      <CustomSelect
                        placeholder="Chọn đơn vị tính"
                        options={[
                          { value: 1, label: 'Toàn chiến dịch' },
                          { value: 2, label: '/1 Ngày' },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                </>
              )}
              <Col xs={24}>
                <Form.List
                  name="banners"
                  initialValue={[
                    {
                      banner_mode: '3',
                      banner_url: '',
                      banner_link: '',
                      banner_max_click_per_day: null,
                      banner_max_impression_per_day: null,
                    },
                  ]}
                >
                  {(fields, { add, remove }) => (
                    <Row gutter={[16, 16]}>
                      <Col xs={24}>
                        <div className="flex flex-row justify-between items-center">
                          <h1 className="text-[20px] font-semibold">Danh sách quảng cáo</h1>
                        </div>
                      </Col>
                      <Col xs={24} className="flex flex-col gap-4">
                        {fields.map(({ key, name, ...restField }, index) => (
                          <Row
                            gutter={[16, 0]}
                            key={key}
                            className="relative"
                            style={{ border: '1px solid black', padding: '20px', borderRadius: '8px' }}
                          >
                            <Col xs={24}>
                              <div className="absolute top-0 right-0 z-10">
                                <Button className="border-none" onClick={() => remove(name)}>
                                  <CloseOutlined className="text-[24px]" />
                                </Button>
                              </div>
                              <Form.Item
                                className="mt-4"
                                {...restField}
                                name={[name, 'banner_url']}
                                label={<FormLabel text="Upload ảnh" />}
                                rules={[
                                  { required: true, message: 'Vui lòng upload file ảnh hoặc nhập link ảnh' },
                                  { type: 'url', message: 'Đường dẫn không hợp lệ' },
                                ]}
                              >
                                <Input
                                  placeholder="Nhập URL hoặc tải lên file..."
                                  addonBefore={
                                    <Upload
                                      accept="image/*"
                                      showUploadList={false}
                                      beforeUpload={(file) => handleUploadFile(file, index)}
                                      className="hover:cursor-pointer"
                                    >
                                      <UploadOutlined />
                                      Upload
                                    </Upload>
                                  }
                                />
                              </Form.Item>
                              {watchBanners?.[index]?.banner_url && (
                                <Image src={watchBanners?.[index]?.banner_url} className="max-h-40" />
                              )}
                            </Col>
                            <Col xs={12}>
                              <Form.Item
                                {...restField}
                                name={[name, 'banner_mode']}
                                label={<FormLabel text="Loại banner/ popup" />}
                                rules={[{ required: true, message: 'Vui lòng chọn loại banner/ popup' }]}
                                initialValue={'3'}
                              >
                                <CustomSelect
                                  placeholder="Chọn loại banner/ popup"
                                  options={[
                                    { value: '1', label: 'Popup' },
                                    { value: '3', label: 'Banner' },
                                  ]}
                                />
                              </Form.Item>
                            </Col>
                            <Col xs={12}>
                              <Form.Item
                                {...restField}
                                name={[name, 'banner_link']}
                                label={<FormLabel text="Link banner/ popup" />}
                                rules={[{ required: true, message: 'Vui lòng nhập link banner/ popup' }]}
                              >
                                <Input allowClear placeholder="Nhập link banner/ popup..." />
                              </Form.Item>
                            </Col>
                            <Col xs={12}>
                              <Form.Item
                                {...restField}
                                name={[name, 'banner_max_click_per_day']}
                                label={<NormalFormLabel text="Số click tối đa/ ngày" />}
                              >
                                <Input allowClear placeholder="Nhập số click tối đa/ ngày..." />
                              </Form.Item>
                            </Col>
                            <Col xs={12}>
                              <Form.Item
                                {...restField}
                                name={[name, 'banner_max_impression_per_day']}
                                label={<NormalFormLabel text="Số impression tối đa/ ngày" />}
                              >
                                <InputNumber type="number" placeholder="Nhập số impression tối đa/ ngày..." />
                              </Form.Item>
                            </Col>
                          </Row>
                        ))}
                      </Col>
                      <Col xs={24}>
                        <div className="flex flex-row justify-end items-center">
                          <Button type="primary" className="flex items-center justify-center" onClick={() => add()}>
                            <PlusOutlined />
                            <span>Thêm ảnh</span>
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  )}
                </Form.List>
              </Col>
              <Col xs={24}>
                <Form.Item
                  label={<FormLabel text="Trạng thái" />}
                  name="status"
                  rules={[{ required: true, message: 'Vui lòng chọn trạng thái' }]}
                  initialValue={0}
                >
                  <CustomSelect
                    placeholder="Chọn trạng thái"
                    options={[LIST_STATUS_CAMPAIGN[0], LIST_STATUS_CAMPAIGN[1]]}
                  />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <div className="flex justify-end gap-3">
                  <Form.Item>
                    <Button onClick={changeBack}>
                      <CloseOutlined /> Hủy bỏ
                    </Button>
                  </Form.Item>
                  <Form.Item>
                    <Button loading={isLoadingCreateCampaign} type="primary" htmlType="submit">
                      <SaveOutlined /> Lưu lại
                    </Button>
                  </Form.Item>
                </div>
              </Col>
            </Row>
          </Form>
        </div>
      )}
    </>
  );
};

export default ThemChienDich;
